import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Checkbox, EmptyState, Field, Input, Select, Spinner, Textarea } from '@aigovops/design-system';
import {
  approveSystemAuthority,
  draftSystemAuthority,
  getSystemAuthority,
  listWorkspaceMembers,
  rejectSystemAuthority,
  updateSystemAuthorityDraft,
  type AuthorityDeclaration,
  type AuthorityVersion,
  type AutonomyLevel,
  type ScopeClass,
  type SystemAuthorityView,
  type WorkspaceMember,
} from '../lib/api';
import { useOptionalAuth } from '../lib/auth';
import { fmtDate } from '../lib/format';
import { useAnnounce } from './today/shared';

/**
 * V111-1-6 (ADR-0092): what this AI system may call and do (declared, SoD-
 * approved), what the connectors actually see (observed), and where the two
 * disagree. Declared items a connector has not seen read as self-attested —
 * never as verified. Nobody who shaped a draft (author or editor) approves it.
 */

const AUTONOMY: Array<{ value: AutonomyLevel; label: string }> = [
  { value: 'informational', label: 'Informational only' },
  { value: 'decision_support', label: 'Suggests; a person decides' },
  { value: 'human_in_the_loop', label: 'Acts after a person approves each action' },
  { value: 'human_on_the_loop', label: 'Acts; a person monitors and can stop it' },
  { value: 'fully_automated', label: 'Acts on its own' },
];
const AUTONOMY_LABEL = Object.fromEntries(AUTONOMY.map((a) => [a.value, a.label])) as Record<AutonomyLevel, string>;

const SCOPES: Array<{ value: ScopeClass; label: string }> = [
  { value: 'read', label: 'Read data' },
  { value: 'write', label: 'Change data' },
  { value: 'execute', label: 'Run code or jobs' },
  { value: 'communication', label: 'Send messages' },
  { value: 'financial', label: 'Move money' },
  { value: 'admin', label: 'Administer systems' },
];
const SCOPE_LABEL = Object.fromEntries(SCOPES.map((s) => [s.value, s.label])) as Record<ScopeClass, string>;

const CHECK_LABEL: Record<string, string> = { 'AS-2': 'Action boundary', 'AS-4': 'Kill switch' };

const card: React.CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface)',
  padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)',
};
const h3: React.CSSProperties = { margin: 0, fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text)' };
const muted: React.CSSProperties = { margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' };

export function SystemAuthorityPanel({ slug, systemId, role }: { slug: string; systemId: string; role: string }) {
  const qc = useQueryClient();
  const me = useOptionalAuth()?.user?.id ?? '';
  const key = ['system-authority', slug, systemId] as const;
  const q = useQuery({ queryKey: key, queryFn: () => getSystemAuthority(slug, systemId) });
  const members = useQuery({ queryKey: ['members', slug], queryFn: () => listWorkspaceMembers(slug) });
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [live, announce] = useAnnounce();
  const canApprove = role === 'owner' || role === 'admin';
  const canDraft = role !== 'viewer';

  async function act(fn: () => Promise<unknown>, done: string, after?: () => void) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: key });
      after?.();
      announce(done);
      setTimeout(() => document.getElementById('auth-status')?.focus(), 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (q.isLoading) return <Spinner label="Loading authority" />;
  if (q.error || !q.data) return <Alert variant="error">{q.error?.message ?? 'Could not load authority.'}</Alert>;
  const v = q.data;
  const base = v.draft ?? v.active;

  const startEditing = () => {
    setError(null);
    setEditing(true);
    setTimeout(() => document.getElementById('auth-form-h')?.focus(), 0);
  };
  const stopEditing = () => {
    setEditing(false);
    setTimeout(() => document.getElementById('auth-status')?.focus(), 0);
  };

  if (editing) {
    return (
      <>
        {live}
        <DeclarationForm
          initial={base}
          members={members.data?.members ?? []}
          busy={busy}
          error={error}
          willDisqualify={!!v.draft && v.draft.created_by_user_id !== me && canApprove}
          onCancel={stopEditing}
          onSubmit={(decl) => act(
            () => (v.draft ? updateSystemAuthorityDraft(slug, systemId, v.draft.authority_id, decl) : draftSystemAuthority(slug, systemId, decl)),
            'Draft saved. It needs approval before it counts.',
            () => setEditing(false),
          )}
        />
      </>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {live}
      <h2 id="auth-status" tabIndex={-1} style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>Agent authority</h2>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <ChecksStrip v={v} />

      {v.drift.classes.length > 0 ? (
        <div style={{ ...card, background: 'var(--warning-bg)', borderColor: 'var(--warning)', color: 'var(--warning-fg)' }}>
          <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)' }}>
            <span aria-hidden="true">⚠ </span>
            The connectors see this system able to {v.drift.classes.map((c) => SCOPE_LABEL[c]?.toLowerCase() ?? c).join(', ')}, which the declaration doesn’t allow.
          </p>
          {canDraft && !v.draft ? <div><Button variant="secondary" compact onClick={startEditing}>Update the declaration</Button></div> : null}
        </div>
      ) : null}

      {v.draft ? (
        <DraftCard
          slug={slug}
          draft={v.draft}
          active={v.active}
          me={me}
          members={members.data?.members ?? []}
          canApprove={canApprove}
          canEdit={canDraft}
          busy={busy}
          onEdit={startEditing}
          onApprove={() => act(() => approveSystemAuthority(slug, systemId, v.draft!.authority_id), `Version ${v.draft!.version} approved and now active.`)}
          onReject={(reason) => act(() => rejectSystemAuthority(slug, systemId, v.draft!.authority_id, reason), 'Draft rejected.')}
        />
      ) : null}

      {v.active ? (
        <DeclaredCard active={v.active} verification={v.verification} canEdit={canDraft && !v.draft} onEdit={startEditing} />
      ) : !v.draft ? (
        <EmptyState
          title="No declared authority"
          description="Declare what this system may call and do. Insurers and assessors read it against what the connectors see."
          action={canDraft ? <Button variant="primary" compact onClick={startEditing}>Declare authority</Button> : undefined}
        />
      ) : null}

      <ObservedCard v={v} />
    </div>
  );
}

const STATUS_WORDS: Record<string, { glyph: string; words: string; fg: string; bg: string }> = {
  pass: { glyph: '✓', words: 'Passing', fg: 'var(--success-fg)', bg: 'var(--success-bg)' },
  fail: { glyph: '✕', words: 'Failing', fg: 'var(--danger-fg)', bg: 'var(--danger-bg)' },
  insufficient_evidence: { glyph: '○', words: 'Can’t check yet', fg: 'var(--warning-fg)', bg: 'var(--warning-bg)' },
};

function ChecksStrip({ v }: { v: SystemAuthorityView }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }} aria-label="Authority checks">
      {v.checks.map((c) => {
        const s = STATUS_WORDS[c.status] ?? STATUS_WORDS.insufficient_evidence!;
        return (
          <li key={c.check_key} style={{ ...card, padding: 'var(--space-3)', gap: 'var(--space-1)', minWidth: '12rem', flex: '1 1 12rem' }}>
            <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>{CHECK_LABEL[c.control_id] ?? c.control_id}</span>
            <span style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)', padding: '2px var(--space-2)', borderRadius: 'var(--radius-chip)', fontWeight: 600, background: s.bg, color: s.fg }}>
              <span aria-hidden="true">{s.glyph}</span>{s.words}
            </span>
            {c.status !== 'pass' ? <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>{checkHint(c)}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

function checkHint(c: SystemAuthorityView['checks'][number]): string {
  const reason = String(c.details.reason ?? '');
  if (reason === 'no_active_declaration') return 'Needs an approved declaration.';
  if (reason === 'declaration_without_substance') return 'Add a prohibited action or a limit.';
  if (reason === 'containment_undeclared') return 'Name a kill switch and its owner.';
  if (reason === 'rollback_evidence_missing' || reason === 'rollback_evidence_unresolved') return 'Link the rollback runbook.';
  if (c.status === 'fail') return 'Observed authority exceeds the declaration.';
  return '';
}

/** Everyone who shaped the draft: its author and every editor (none may approve). */
function contributors(draft: AuthorityVersion): string[] {
  let editors: string[] = [];
  try {
    const e = JSON.parse(draft.edited_by_json ?? '[]');
    editors = Array.isArray(e) ? e.map(String) : [];
  } catch { /* none */ }
  return [draft.created_by_user_id, ...editors];
}

function DraftCard({ slug, draft, active, me, members, canApprove, canEdit, busy, onEdit, onApprove, onReject }: {
  slug: string; draft: AuthorityVersion; active: AuthorityVersion | null; me: string; members: WorkspaceMember[];
  canApprove: boolean; canEdit: boolean; busy: boolean;
  onEdit: () => void; onApprove: () => void; onReject: (reason: string) => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const shapers = contributors(draft);
  const iShapedIt = shapers.includes(me);
  const nameOf = (id: string) => members.find((m) => m.user_id === id)?.display_name ?? 'a teammate';
  const eligible = members.filter((m) => (m.role === 'owner' || m.role === 'admin') && !shapers.includes(m.user_id));
  const canIApprove = canApprove && !iShapedIt;

  return (
    <section style={{ ...card, borderColor: 'var(--accent)' }} aria-labelledby="auth-draft-h">
      <h3 id="auth-draft-h" style={h3}>Draft version {draft.version} awaiting approval</h3>
      <p style={muted}>Drafted by {draft.created_by_user_id === me ? 'you' : nameOf(draft.created_by_user_id)} · {fmtDate(draft.created_at)}</p>

      <DeclarationDiff from={active} to={draft} />

      {canIApprove ? null : (
        <p style={{ ...muted, color: 'var(--text)' }}>
          {iShapedIt ? 'You shaped this draft, so someone else approves it. ' : 'Approval: an owner or admin who didn’t write or edit it. '}
          {eligible.length > 0
            ? <>Who can: {eligible.map((m) => m.display_name).join(', ')}.</>
            : <>No one else can approve yet. <Link to={`/w/${slug}/app/team`}>Invite an admin</Link></>}
        </p>
      )}

      {rejecting ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <Field id="auth-reject-reason" label="Why reject it?">
            <Textarea id="auth-reject-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="danger" compact disabled={busy || !reason.trim()} onClick={() => onReject(reason.trim())}>Reject draft</Button>
            <Button variant="ghost" compact disabled={busy} onClick={() => setRejecting(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {canIApprove ? <Button variant="primary" compact disabled={busy} onClick={onApprove}>Approve version {draft.version}</Button> : null}
          {canIApprove ? <Button variant="secondary" compact disabled={busy} onClick={() => setRejecting(true)}>Reject</Button> : null}
          {canEdit ? <Button variant="secondary" compact disabled={busy} onClick={onEdit}>Edit draft</Button> : null}
        </div>
      )}
    </section>
  );
}

type Change =
  | { kind: 'list'; field: string; added: Array<{ text: string; risky: boolean }>; removed: string[] }
  | { kind: 'value'; field: string; from: string; to: string };

const RISKY_SCOPES: ReadonlySet<string> = new Set(['financial', 'admin']);

/**
 * What approving changes — the approver never signs blind. List fields show
 * each item added (+) or removed (−); an added high-risk capability is flagged.
 */
function DeclarationDiff({ from, to }: { from: AuthorityVersion | null; to: AuthorityVersion }) {
  const changes = useMemo<Change[]>(() => {
    const a = itemsOf(from);
    const b = itemsOf(to);
    const out: Change[] = [];
    for (const k of Object.keys(FIELD_LABEL) as Array<keyof typeof FIELD_LABEL>) {
      const x = a[k];
      const y = b[k];
      if (Array.isArray(x) && Array.isArray(y)) {
        const xs = new Set(x.map((i) => i.text));
        const ys = new Set(y.map((i) => i.text));
        const added = y.filter((i) => !xs.has(i.text));
        const removed = x.filter((i) => !ys.has(i.text)).map((i) => i.text);
        if (added.length || removed.length) out.push({ kind: 'list', field: FIELD_LABEL[k], added, removed });
      } else if (x !== y) {
        out.push({ kind: 'value', field: FIELD_LABEL[k], from: (x as string) || '—', to: (y as string) || '—' });
      }
    }
    return out;
  }, [from, to]);
  if (changes.length === 0) {
    return <p style={muted}>{from ? 'Same as the active version.' : 'First declaration for this system.'}</p>;
  }
  const cell: React.CSSProperties = { verticalAlign: 'top', padding: 'var(--space-1) var(--space-2)' };
  return (
    <div>
      <h4 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)' }}>
        {from ? `What changes from version ${from.version}` : 'What it declares'}
      </h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-2xs)' }}>
        <tbody>
          {changes.map((c) => (
            <tr key={c.field} style={{ borderTop: '1px solid var(--border)' }}>
              <th scope="row" style={{ ...cell, textAlign: 'left', fontWeight: 500, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{c.field}</th>
              <td style={{ ...cell, color: 'var(--text)' }}>
                {c.kind === 'value' ? (
                  from ? <>{c.from} <span aria-hidden="true">→</span><span className="sr-only"> changes to </span> <strong>{c.to}</strong></> : c.to
                ) : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '2px' }}>
                    {c.added.map((i) => (
                      <li key={`+${i.text}`} style={{ color: i.risky ? 'var(--warning-fg)' : 'var(--success-fg)', fontWeight: 600 }}>
                        <span aria-hidden="true">{i.risky ? '⚠ + ' : '+ '}</span>
                        <span className="sr-only">{i.risky ? 'Added, high risk: ' : 'Added: '}</span>{i.text}
                      </li>
                    ))}
                    {c.removed.map((t) => (
                      <li key={`-${t}`} style={{ color: 'var(--danger-fg)' }}>
                        <span aria-hidden="true">− </span><span className="sr-only">Removed: </span>
                        <span style={{ textDecoration: 'line-through' }}>{t}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type Item = { text: string; risky: boolean };

/** The declaration as comparable items: lists stay lists so the diff can name each addition and removal. */
function itemsOf(v: AuthorityVersion | null): Record<keyof typeof FIELD_LABEL, string | Item[]> {
  const s = summarize(v);
  if (!v) return { ...s, tools: [], allowed: [], never: [] };
  const tools = parse<AuthorityDeclaration['tools']>(v.tools_json, []);
  const c = parse<AuthorityDeclaration['action_ceiling']>(v.action_ceiling_json, {});
  return {
    ...s,
    tools: tools.map((t) => ({ text: `${t.name} (${SCOPE_LABEL[t.scope_class]?.toLowerCase() ?? t.scope_class})`, risky: RISKY_SCOPES.has(t.scope_class) })),
    allowed: (c.authorized_action_classes ?? []).map((x) => ({ text: SCOPE_LABEL[x] ?? x, risky: RISKY_SCOPES.has(x) })),
    never: (c.prohibited_actions ?? []).map((x) => ({ text: x, risky: false })),
  };
}

const FIELD_LABEL = {
  autonomy: 'Independence', tools: 'Tools', allowed: 'Allowed to', never: 'Never does', limits: 'Limits', kill: 'Kill switch', rollback: 'Rollback runbook',
} as const;

function summarize(v: AuthorityVersion | null): Record<keyof typeof FIELD_LABEL, string> {
  if (!v) return { autonomy: '', tools: '', allowed: '', never: '', limits: '', kill: '', rollback: '' };
  const tools = parse<AuthorityDeclaration['tools']>(v.tools_json, []);
  const c = parse<AuthorityDeclaration['action_ceiling']>(v.action_ceiling_json, {});
  const k = parse<AuthorityDeclaration['containment']>(v.containment_json, {});
  const l = c.limits ?? {};
  return {
    autonomy: AUTONOMY_LABEL[v.autonomy_level] ?? v.autonomy_level,
    tools: tools.map((t) => `${t.name} (${SCOPE_LABEL[t.scope_class]?.toLowerCase() ?? t.scope_class})`).join(', '),
    allowed: (c.authorized_action_classes ?? []).map((s) => SCOPE_LABEL[s] ?? s).join(', '),
    never: (c.prohibited_actions ?? []).join('; '),
    limits: [l.spend_usd != null ? `$${l.spend_usd} spend` : '', l.rows != null ? `${l.rows} records` : '', l.recipients != null ? `${l.recipients} recipients` : ''].filter(Boolean).join(' · '),
    kill: k.kill_switch_kind ?? '',
    rollback: k.rollback_evidence_ref ? 'Linked' : '',
  };
}

function DeclaredCard({ active, verification, canEdit, onEdit }: {
  active: AuthorityVersion; verification: SystemAuthorityView['verification']; canEdit: boolean; onEdit: () => void;
}) {
  const s = useMemo(() => summarize(active), [active]);
  return (
    <section style={card} aria-labelledby="auth-declared-h">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'baseline' }}>
        <h3 id="auth-declared-h" style={h3}>Declared · version {active.version}</h3>
        {canEdit ? <Button variant="ghost" compact onClick={onEdit}>Propose a change</Button> : null}
      </div>
      <p style={{ ...muted, color: 'var(--text)' }}>{s.autonomy}</p>
      {verification.length > 0 ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-1)' }} aria-label="Declared tools and actions">
          {verification.map((x) => (
            <li key={`${x.kind}-${x.name}`} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap', fontSize: 'var(--font-size-2xs)' }}>
              <span style={{ color: 'var(--text)' }}>{x.kind === 'tool' ? x.name : SCOPE_LABEL[x.scope_class]}</span>
              {x.kind === 'tool' ? <span style={{ color: 'var(--text-dim)' }}>{SCOPE_LABEL[x.scope_class]}</span> : null}
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
                <span aria-hidden="true">{x.verified ? '✓ ' : '○ '}</span>{x.verified ? 'Seen by a connector' : 'Self-attested'}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      <dl style={{ margin: 0, display: 'grid', gap: 'var(--space-1)', gridTemplateColumns: 'max-content 1fr', columnGap: 'var(--space-3)', fontSize: 'var(--font-size-2xs)' }}>
        {(['never', 'limits', 'kill', 'rollback'] as const).map((k) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt style={{ color: 'var(--text-dim)' }}>{FIELD_LABEL[k]}</dt>
            <dd style={{ margin: 0, color: 'var(--text)' }}>{s[k] || '—'}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function ObservedCard({ v }: { v: SystemAuthorityView }) {
  const live = v.observed.filter((f) => !f.gone_at && f.fact_kind !== 'authority_read_unavailable');
  const gaps = v.observed.filter((f) => f.fact_kind === 'authority_read_unavailable');
  return (
    <section style={card} aria-labelledby="auth-observed-h">
      <h3 id="auth-observed-h" style={h3}>Seen by connectors</h3>
      {live.length === 0 ? (
        <p style={muted}>No connector reports this system’s tools or permissions yet. Connect the cloud account it runs in to compare.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-1)' }}>
          {live.map((f) => (
            <li key={f.fact_id} style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', fontSize: 'var(--font-size-2xs)' }}>
              <span style={{ color: 'var(--text)' }}>{f.name}</span>
              <span style={{ color: 'var(--text-dim)' }}>{f.scope_class === 'unknown' ? 'Unclassified' : SCOPE_LABEL[f.scope_class]}</span>
              {f.summary_confidence === 'approximate' ? <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>approximate</span> : null}
            </li>
          ))}
        </ul>
      )}
      {gaps.length > 0 ? (
        <p style={muted}>
          <span aria-hidden="true">○ </span>Couldn’t read authority from {[...new Set(gaps.map((g) => g.provider_key ?? g.name))].join(', ')}. What’s declared there stays self-attested.
        </p>
      ) : null}
    </section>
  );
}

function DeclarationForm({ initial, members, busy, error, willDisqualify, onCancel, onSubmit }: {
  initial: AuthorityVersion | null; members: WorkspaceMember[]; busy: boolean; error: string | null; willDisqualify: boolean;
  onCancel: () => void; onSubmit: (d: AuthorityDeclaration) => void;
}) {
  const ceiling0 = parse<AuthorityDeclaration['action_ceiling']>(initial?.action_ceiling_json ?? '{}', {});
  const contain0 = parse<AuthorityDeclaration['containment']>(initial?.containment_json ?? '{}', {});
  const [autonomy, setAutonomy] = useState<AutonomyLevel>(initial?.autonomy_level ?? 'human_in_the_loop');
  const [tools, setTools] = useState<AuthorityDeclaration['tools']>(parse(initial?.tools_json ?? '[]', []));
  const [classes, setClasses] = useState<ScopeClass[]>(ceiling0.authorized_action_classes ?? ['read']);
  const [prohibited, setProhibited] = useState((ceiling0.prohibited_actions ?? []).join('\n'));
  const [spend, setSpend] = useState(ceiling0.limits?.spend_usd != null ? String(ceiling0.limits.spend_usd) : '');
  const [kill, setKill] = useState(contain0.kill_switch_kind ?? '');
  const [owner, setOwner] = useState(contain0.kill_switch_owner_user_id ?? '');
  const [rollback, setRollback] = useState(contain0.rollback_evidence_ref ?? '');

  function submit() {
    // Fields this form doesn't show (record / recipient limits, fallback mode,
    // monitoring owner) are carried over, never silently dropped.
    const limits = { ...(ceiling0.limits ?? {}) };
    if (spend.trim()) limits.spend_usd = Number(spend); else delete limits.spend_usd;
    onSubmit({
      autonomy_level: autonomy,
      tools: tools.filter((t) => t.name.trim()),
      action_ceiling: {
        authorized_action_classes: classes,
        prohibited_actions: prohibited.split('\n').map((s) => s.trim()).filter(Boolean),
        ...(Object.keys(limits).length ? { limits } : {}),
      },
      containment: {
        ...contain0,
        kill_switch_kind: kill.trim() || undefined,
        kill_switch_owner_user_id: owner || undefined,
        rollback_evidence_ref: rollback.trim() || undefined,
      },
      monitoring_owner_user_id: initial?.monitoring_owner_user_id ?? null,
    });
  }

  return (
    <section style={card} aria-labelledby="auth-form-h">
      <h3 id="auth-form-h" style={h3} tabIndex={-1}>{initial?.status === 'draft' ? 'Edit draft' : 'Declare authority'}</h3>
      <p style={muted}>
        Saved as a draft. An owner or admin who didn’t write or edit it approves it before it counts.
        {willDisqualify ? ' If you save changes here, someone else approves it.' : ''}
      </p>
      <Field id="auth-autonomy" label="How independently it acts">
        <Select id="auth-autonomy" value={autonomy} onChange={(e) => setAutonomy(e.target.value as AutonomyLevel)}>
          {AUTONOMY.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </Select>
      </Field>

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
        <legend style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>Tools it can call</legend>
        {tools.map((t, i) => (
          <div key={i} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'end', flexWrap: 'wrap' }}>
            <Field id={`auth-tool-${i}`} label="Tool">
              <Input id={`auth-tool-${i}`} value={t.name} onChange={(e) => setTools(tools.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
            </Field>
            <Field id={`auth-tool-scope-${i}`} label="What it can do">
              <Select id={`auth-tool-scope-${i}`} value={t.scope_class} onChange={(e) => setTools(tools.map((x, j) => (j === i ? { ...x, scope_class: e.target.value as ScopeClass } : x)))}>
                {SCOPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </Field>
            <Button variant="ghost" compact onClick={() => setTools(tools.filter((_, j) => j !== i))}>Remove {t.name || `tool ${i + 1}`}</Button>
          </div>
        ))}
        <div><Button variant="secondary" compact onClick={() => setTools([...tools, { name: '', scope_class: 'read' }])}>+ Add tool</Button></div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
        <legend style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>Allowed to</legend>
        <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          {SCOPES.map((s) => (
            <Checkbox key={s.value} label={s.label} checked={classes.includes(s.value)}
              onChange={(e) => setClasses(e.target.checked ? [...classes, s.value] : classes.filter((c) => c !== s.value))} />
          ))}
        </div>
      </fieldset>

      <Field id="auth-prohibited" label="Never does (one per line)">
        <Textarea id="auth-prohibited" rows={3} value={prohibited} onChange={(e) => setProhibited(e.target.value)} />
      </Field>
      <div style={{ display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))' }}>
        <Field id="auth-spend" label="Spend limit (USD, optional)">
          <Input id="auth-spend" inputMode="decimal" value={spend} onChange={(e) => setSpend(e.target.value)} />
        </Field>
        <Field id="auth-kill" label="Kill switch">
          <Input id="auth-kill" value={kill} onChange={(e) => setKill(e.target.value)} placeholder="e.g. Disable at the AI gateway" />
        </Field>
        <Field id="auth-owner" label="Kill switch owner">
          <Select id="auth-owner" value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">Choose a person</option>
            {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.display_name}</option>)}
          </Select>
        </Field>
        <Field id="auth-rollback" label="Rollback runbook (evidence)">
          <Input id="auth-rollback" value={rollback} onChange={(e) => setRollback(e.target.value)} />
        </Field>
      </div>

      {error ? <Alert variant="error">{error}</Alert> : null}
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Button variant="primary" compact disabled={busy} onClick={submit}>{busy ? 'Saving…' : 'Save draft'}</Button>
        <Button variant="ghost" compact disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </section>
  );
}

function parse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}
